import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { SectionHeading } from "@/components/Brand";
import Lightbox from "@/components/Lightbox";
import { FramedImage } from "@/components/SmartImage";

export default function Portfolio() {
  const { t, localized } = useI18n();
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const visible = active === "all" ? projects : projects.filter((p) => p.slug === active);
  const items = visible.flatMap((p) =>
    (p.images || []).map((src, i) => ({
      id: `${p.slug}-${i}`,
      external_url: src,
      filename: `${p.slug}-${i + 1}.jpg`,
      kind: "photo",
      photographer: "HR Creations",
      capture_date: p.date,
    }))
  );

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-14 sm:px-8 lg:px-12 lg:py-20">
      <SectionHeading label="HR Creations" title={t("portfolio.title")} sub={t("portfolio.sub")} />

      <div className="mt-8 flex flex-wrap gap-2" data-testid="portfolio-filters">
        {[{ slug: "all", label: t("portfolio.all") }, ...projects.map((p) => ({ slug: p.slug, label: localized(p, "category") }))].map(
          (f) => (
            <button
              key={f.slug}
              type="button"
              data-testid={`portfolio-filter-${f.slug}`}
              onClick={() => setActive(f.slug)}
              className={`min-h-[44px] border px-4 text-sm transition-colors duration-300 ${
                active === f.slug
                  ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]"
                  : "border-[#C69A3B]/50 text-[#252525] hover:border-[#8F3329]"
              }`}
            >
              {f.label}
            </button>
          )
        )}
      </div>

      {visible.map((p) => (
        <section key={p.slug} className="mt-14" data-testid={`portfolio-section-${p.slug}`}>
          <h2 className="font-serif-display text-2xl text-[#8F3329]">{localized(p, "category")}</h2>
          <p className="mt-2 max-w-xl text-sm text-[#5A5752]">{localized(p, "description")}</p>
          <div className="masonry mt-7">
            {(p.images || []).map((src, i) => {
              const idx = items.findIndex((it) => it.id === `${p.slug}-${i}`);
              return (
                <button
                  key={src + i}
                  type="button"
                  data-testid={`portfolio-image-${p.slug}-${i}`}
                  onClick={() => setLightbox(idx)}
                  className="photo-hover block w-full overflow-hidden border border-[#C69A3B]/25 bg-white"
                >
                  <FramedImage src={src} alt={localized(p, "category")} loading="lazy" className="w-full" />
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {lightbox !== null && (
        <Lightbox items={items} index={lightbox} onClose={() => setLightbox(null)} onIndexChange={setLightbox} />
      )}
    </div>
  );
}
